import { useEffect, useState } from "react"
import { NIGERIAN_STATES } from "@client/lib/nigerian-states"
import { toast } from "sonner"
import { Modal, ModalActions, ModalButton } from "./modal"
import { Select } from "./select"

export type SchoolType = "primary" | "secondary" | "combined"

export interface SchoolFormValues {
  name: string
  address: string
  city: string
  state: string
  lga: string
  enrollment: string
  type: SchoolType
  contactName: string
  contactTitle: string
  contactPhone: string
  contactEmail: string
  notes: string
}

export const EMPTY_SCHOOL_FORM: SchoolFormValues = {
  name: "",
  address: "",
  city: "",
  state: "Lagos",
  lga: "",
  enrollment: "",
  type: "primary",
  contactName: "",
  contactTitle: "",
  contactPhone: "",
  contactEmail: "",
  notes: "",
}

const SCHOOL_TYPE_LABELS: Record<SchoolType, string> = {
  primary: "Primary",
  secondary: "Secondary",
  combined: "Combined",
}

interface SchoolFormContentProps {
  values: SchoolFormValues
  onChange: (values: SchoolFormValues) => void
  onSubmit: () => void | Promise<void>
  onCancel: () => void
  onBack?: () => void
  saving?: boolean
  submitLabel?: string
  backLabel?: string
}

export function SchoolFormContent({
  values,
  onChange,
  onSubmit,
  onCancel,
  onBack,
  saving = false,
  submitLabel = "Save",
  backLabel = "Back",
}: SchoolFormContentProps) {
  const update = <K extends keyof SchoolFormValues>(field: K, value: SchoolFormValues[K]) => {
    onChange({ ...values, [field]: value })
  }

  const handleSubmit = () => {
    if (!values.name.trim() || !values.city.trim() || !values.state.trim() || !values.contactName.trim()) {
      toast.error("Please fill in school name, city, state, and contact name")
      return
    }
    void onSubmit()
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-ink">
          School Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={values.name}
          onChange={(event) => update("name", event.target.value)}
          placeholder="Enter school name"
          className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
        />
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-ink">Address</label>
        <input
          type="text"
          value={values.address}
          onChange={(event) => update("address", event.target.value)}
          placeholder="Street address"
          className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={values.city}
            onChange={(event) => update("city", event.target.value)}
            placeholder="City"
            className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">
            State <span className="text-red-500">*</span>
          </label>
          <Select value={values.state} onChange={(value) => update("state", value)} options={NIGERIAN_STATES} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">LGA</label>
          <input
            type="text"
            value={values.lga}
            onChange={(event) => update("lga", event.target.value)}
            placeholder="Local govt area"
            className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Enrollment</label>
          <input
            type="number"
            value={values.enrollment}
            onChange={(event) => update("enrollment", event.target.value)}
            placeholder="0"
            min="0"
            className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">School Type</label>
          <Select
            value={SCHOOL_TYPE_LABELS[values.type]}
            onChange={(value) => {
              const entry = Object.entries(SCHOOL_TYPE_LABELS).find(([, label]) => label === value)
              if (entry) update("type", entry[0] as SchoolType)
            }}
            options={Object.values(SCHOOL_TYPE_LABELS)}
          />
        </div>
      </div>

      <div className="border-t border-gold-500/50 pt-4">
        <h3 className="mb-3 text-sm font-semibold text-ink">Contact Person</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={values.contactName}
              onChange={(event) => update("contactName", event.target.value)}
              placeholder="Contact name"
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Title</label>
            <input
              type="text"
              value={values.contactTitle}
              onChange={(event) => update("contactTitle", event.target.value)}
              placeholder="e.g., Principal"
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Phone</label>
            <input
              type="tel"
              value={values.contactPhone}
              onChange={(event) => update("contactPhone", event.target.value)}
              placeholder="+234..."
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Email</label>
            <input
              type="email"
              value={values.contactEmail}
              onChange={(event) => update("contactEmail", event.target.value)}
              placeholder="email@school.com"
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-ink">Notes</label>
        <textarea
          value={values.notes}
          onChange={(event) => update("notes", event.target.value)}
          placeholder="Additional notes about this school..."
          rows={3}
          className="w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
        />
      </div>

      <ModalActions>
        {onBack && <ModalButton onClick={onBack} disabled={saving}>{backLabel}</ModalButton>}
        <ModalButton onClick={onCancel} disabled={saving}>Cancel</ModalButton>
        <ModalButton onClick={handleSubmit} variant="primary" disabled={saving}>
          {saving ? "Saving..." : submitLabel}
        </ModalButton>
      </ModalActions>
    </div>
  )
}

interface SchoolFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (values: SchoolFormValues) => Promise<void>
  initialValues: SchoolFormValues
  title?: string
  subtitle?: string
  submitLabel?: string
}

export function SchoolFormModal({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  title = "Add School",
  subtitle,
  submitLabel = "Add School",
}: SchoolFormModalProps) {
  const [values, setValues] = useState(initialValues)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setValues(initialValues)
      setSaving(false)
    }
  }, [initialValues, isOpen])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} subtitle={subtitle} maxWidth="lg">
      <SchoolFormContent
        values={values}
        onChange={setValues}
        onSubmit={async () => {
          setSaving(true)
          try {
            await onSubmit(values)
          } finally {
            setSaving(false)
          }
        }}
        onCancel={onClose}
        saving={saving}
        submitLabel={submitLabel}
      />
    </Modal>
  )
}
